/**
 * The signed-in recording's upload, as a **module-scoped task** rather than
 * state inside a React component.
 *
 * The recorder on `/app/record` starts the upload, and the person may walk to
 * the dashboard long before it finishes. A component that owned the upload
 * would take it down with that navigation; a module-scoped task runs for the
 * life of the tab:
 *
 * - the run's state is module state (`reflectionUploadStore.ts`), published to
 *   subscribers;
 * - `useResumableUpload` is a `useSyncExternalStore` view over it, so
 *   unmounting a subscriber stops the subscription and never the work;
 * - the only way to cancel is {@link abortReflectionUpload}, an explicit act
 *   rather than a side effect of navigation;
 * - the run (`performUpload.ts`) emits its own terminal telemetry, so
 *   `upload_succeeded` and `upload_failed` fire once per run whatever is on
 *   screen.
 *
 * Module scope, rather than a provider in the app layout, because it survives
 * a remount of any segment as well as a soft navigation, and is testable in
 * node without React. The anonymous funnel's take is shaped the same way for
 * the same reason (`lib/onboarding/onboardingUploadTask.ts`).
 *
 * One recording at a time: {@link startReflectionUpload}'s once-guard keeps a
 * double-tap from minting two reflections out of one recording, and a
 * superseding `start()`/`retry()` aborts the run in flight before beginning its
 * own, so two runs can never race to `finalize`.
 */
import {
  normalizeUploadContentType,
  UPLOAD_CHUNK_SIZE_BYTES,
  UPLOAD_CHUNK_STALL_TIMEOUT_MS,
  UPLOAD_CHUNK_TIMEOUT_MS,
} from "@reflect/domain";

import { canStreamChunkSize, startLiveUpload } from "./liveUpload";
import { performUpload } from "./performUpload";
import { patch, publishIdle, task, type ReflectionUploadDeps } from "./reflectionUploadStore";
import type { StartOptions } from "./types";
import { UploadAbortedError } from "./types";
import { xhrTransport } from "./xhrTransport";

export {
  getReflectionUploadServerSnapshot,
  getReflectionUploadSnapshot,
  subscribeReflectionUpload,
} from "./reflectionUploadStore";
export type { ReflectionUploadDeps, ReflectionUploadSnapshot } from "./reflectionUploadStore";

/**
 * Begin uploading `recording`. Idempotent within one recording via the
 * once-guard — a double-tapped Finish mints one reflection, not two.
 *
 * @param recording - the assembled take.
 * @param options - duration and title, forwarded to both mutations.
 * @param runDeps - the cap, the tRPC caller and the test seams; retained for
 *   the life of the run so a later {@link retryReflectionUpload} still has them.
 */
export function startReflectionUpload(
  recording: Blob,
  options: StartOptions,
  runDeps: ReflectionUploadDeps,
): void {
  if (task.started) return;
  task.started = true;
  // The stream of this take, if it had one, now belongs to this run.
  const source = task.live;
  task.live = null;
  task.blob = recording;
  task.startOptions = options;
  task.deps = runDeps;
  task.owner = runDeps.owner;
  task.progressHighWater = 0;
  const begunAt = (runDeps.now ?? Date.now)();
  patch({
    progress: 0,
    totalBytes: null,
    title: options.title ?? null,
    startedAt: begunAt,
    // Seeded rather than left null: the watchdog asks how long the upload has
    // been standing still, and an upload that has just begun has not been
    // standing still at all. A null would have to be special-cased by every
    // reader into "use startedAt", which is this, written once.
    lastProgressAt: begunAt,
  });
  void performUpload(recording, options, source);
}

/**
 * Re-upload the same recording under a **fresh** session. The once-guard
 * stays set.
 *
 * Reachable from the dashboard's pending card as well as the recorder, which is
 * the point of holding the recording and the deps here: by then the screen that
 * started the upload is long gone.
 *
 * @param overrides - deps to refresh for this attempt; everything omitted keeps
 *   the run's own. The recorder's retry passes its whole current set, so a
 *   still-mounted screen re-runs against its freshest entitlement. The
 *   dashboard's passes none: by then there is no recorder to ask, and the
 *   retained `uploadApi` is a vanilla tRPC client that takes its token at call
 *   time, so it is still the right caller for whoever is signed in — which
 *   {@link reconcileReflectionUploadOwner} guarantees is still the owner.
 */
export function retryReflectionUpload(overrides?: Partial<ReflectionUploadDeps>): void {
  const recording = task.blob;
  if (recording === null || task.deps === null) return;
  if (overrides !== undefined) task.deps = { ...task.deps, ...overrides };
  task.progressHighWater = 0;
  const begunAt = (task.deps.now ?? Date.now)();
  patch({ progress: 0, startedAt: begunAt, lastProgressAt: begunAt });
  // Never a stream: a retry mints a fresh session and re-sends the lot.
  void performUpload(recording, task.startOptions, null);
}

/**
 * Cancel the upload in flight.
 *
 * The **only** cancellation channel: no component's teardown is one.
 */
export function abortReflectionUpload(): void {
  task.controller?.abort(new UploadAbortedError());
}

/**
 * Clear the once-guard and release the recording, ready for the next one.
 *
 * Aborts any live phase rather than refusing, then re-arms the guard. Bumping
 * the run id silences the aborted run's late catch so it cannot overwrite the
 * idle state published here.
 */
export function resetReflectionUploadTask(): void {
  task.controller?.abort(new UploadAbortedError());
  task.controller = null;
  task.live?.abandon();
  task.live = null;
  task.runId += 1;
  task.started = false;
  task.owner = null;
  task.blob = null;
  task.startOptions = {};
  task.deps = null;
  task.progressHighWater = 0;
  publishIdle();
}

/**
 * Drop the retained recording when the tab changes hands.
 *
 * Sign-out and sign-in are a *keyed remount* of the session subtree plus a
 * cache teardown (`AuthSessionBoundary`), and neither reaches module scope.
 * Without this, user A's recording, snapshot and deps would survive into user
 * B's session in the same tab: B's dashboard would render A's pending card — a
 * recording B never made — and Retry would re-run it against a tRPC client
 * whose tokens come from `provider.getIdToken()` *now*, which is B, committing
 * A's recording to B's account.
 *
 * Both halves matter and the privacy one is the wider of the two: even with no
 * retry, holding a stranger's video in the tab after they signed out is not
 * something to leave to whether anyone clicks.
 *
 * Retention is asked of the once-guard, not of the owner, so an upload somehow
 * begun with no identity is cleared by the first identity that resolves rather
 * than being adopted by it. Callers pass only RESOLVED identities: `loading` is
 * the first frames of every visit and `unavailable` is an answer we never got,
 * and clearing on either would destroy a live upload over a slow SDK chunk.
 *
 * @param uid - the provider uid now signed in, or `null` for signed out.
 */
export function reconcileReflectionUploadOwner(uid: string | null): void {
  if (!task.started) return;
  if (uid === task.owner) return;
  resetReflectionUploadTask();
}

/**
 * Begin streaming a take to the server as it records.
 *
 * Called by the recorder when a take starts. Best-effort by construction: it
 * does nothing when there is no stream worth attempting, and what it starts
 * can only ever hand the post-record run a head start (see `liveUpload.ts`).
 * Abandons any earlier take's stream, since only the newest take can be the
 * one that gets uploaded.
 *
 * @param mimeType - the recorder's negotiated MIME type.
 * @param runDeps - the same deps `start()` will be handed.
 */
export function beginLiveReflectionUpload(mimeType: string, runDeps: ReflectionUploadDeps): void {
  task.live?.abandon();
  task.live = null;
  // A retained run means this take's `start()` will be refused by the
  // once-guard, so there is nothing its stream could ever be handed to.
  if (task.started) return;
  const contentType = normalizeUploadContentType(mimeType);
  const chunkSizeBytes = runDeps.chunkSizeBytes ?? UPLOAD_CHUNK_SIZE_BYTES;
  if (contentType === null || !canStreamChunkSize(chunkSizeBytes)) return;
  task.live = startLiveUpload({
    contentType,
    createSession: () => runDeps.uploadApi.createResumableSession({ contentType }),
    chunkSizeBytes,
    maxUploadBytes: runDeps.maxUploadBytes,
    timeoutMs: UPLOAD_CHUNK_TIMEOUT_MS,
    stallTimeoutMs: UPLOAD_CHUNK_STALL_TIMEOUT_MS,
    transport: runDeps.transport ?? xhrTransport,
  });
}

/**
 * Feed one recorder chunk to the take being streamed, if there is one.
 *
 * @param chunk - a `dataavailable` chunk, in the order the recording is
 *   assembled from.
 */
export function appendLiveReflectionUpload(chunk: Blob): void {
  task.live?.append(chunk);
}

/**
 * Drop the take being streamed without starting an upload — the take was
 * thrown away (a retake, a recorder error). Leaves any run in flight alone.
 */
export function abandonLiveReflectionUpload(): void {
  task.live?.abandon();
  task.live = null;
}
